import { Package, Clock, Gift, Phone, Timer, Zap, Loader2 } from 'lucide-react';
import { Contact, LeadTemperature } from '@/types/crm';
import { cn } from '@/lib/utils';
import { formatDistanceToNow } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import { useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';

// WhatsApp icon component
const WhatsAppIcon = ({ size = 11, className }: { size?: number; className?: string }) => (
  <svg 
    viewBox="0 0 24 24" 
    width={size} 
    height={size} 
    fill="currentColor"
    className={className}
  >
    <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
  </svg>
);

type ViewMode = 'kanban' | 'list';

interface LeadCardProps {
  contact: Contact;
  onClick?: () => void;
  viewMode?: ViewMode;
  stageColor?: string;
  clientId?: string;
}

const getScoreStyle = (score: number) => {
  if (score >= 70) return { bg: 'rgba(34, 197, 94, 0.2)', border: 'rgba(34, 197, 94, 0.4)', color: '#22C55E' };
  if (score >= 40) return { bg: 'rgba(245, 158, 11, 0.2)', border: 'rgba(245, 158, 11, 0.4)', color: '#F59E0B' };
  return { bg: 'rgba(148, 163, 184, 0.2)', border: 'rgba(148, 163, 184, 0.4)', color: '#94A3B8' };
};

const temperatureConfig: Record<LeadTemperature, { emoji: string; className: string; color: string }> = {
  quente: { emoji: '🔥', className: 'bg-temp-hot/20 text-temp-hot border-temp-hot/40', color: '#EF4444' },
  morno: { emoji: '🌤️', className: 'bg-temp-warm/20 text-temp-warm border-temp-warm/40', color: '#F59E0B' },
  frio: { emoji: '❄️', className: 'bg-temp-cold/20 text-temp-cold border-temp-cold/40', color: '#3B82F6' }
};

// Helper to format time ago
const getTimeAgo = (dateString?: string): string | null => {
  if (!dateString) return null;
  try {
    const date = new Date(dateString);
    if (isNaN(date.getTime())) return null;
    return formatDistanceToNow(date, { addSuffix: true, locale: ptBR });
  } catch {
    return null;
  }
};

// Helper to get time in stage based on actual dates
const getTimeInStage = (contact: Contact): string => {
  // Regra: "tempo na etapa" = primeira atualização / primeiro contato
  const dateStr = contact.data_primeira_mensagem;
  if (!dateStr) return 'Recém-adicionado';
  
  try {
    const date = new Date(dateStr);
    if (isNaN(date.getTime())) return 'Recém-adicionado';
    
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
    const diffDays = Math.floor(diffHours / 24);
    const remainingHours = diffHours % 24;
    
    if (diffDays > 0) {
      return `${diffDays}d ${remainingHours}h`;
    }
    if (diffHours > 0) {
      return `${diffHours}h`;
    }
    const diffMinutes = Math.floor(diffMs / (1000 * 60));
    return diffMinutes > 0 ? `${diffMinutes}min` : 'Agora';
  } catch {
    return 'Recém-adicionado';
  }
};

// Helper to build WhatsApp URL
const getWhatsAppUrl = (telefone: string | undefined): string | null => {
  if (!telefone) return null;
  const cleanNumber = String(telefone).replace(/\D/g, '');
  if (!cleanNumber) return null;
  const fullNumber = cleanNumber.startsWith('55') ? cleanNumber : `55${cleanNumber}`;
  return `https://wa.me/${fullNumber}`;
};

export const LeadCard = ({ contact, onClick, viewMode = 'kanban', stageColor = '#7C3AED', clientId }: LeadCardProps) => {
  const [score, setScore] = useState<number | null>(null);
  const [calculatingScore, setCalculatingScore] = useState(false);
  
  const temp = contact.temperatura_lead?.toLowerCase() as LeadTemperature | undefined;
  const tempConfig = temp ? temperatureConfig[temp] : null;
  const tempColor = tempConfig?.color || stageColor;
  
  // Regra: "embaixo" = última atualização
  const timeAgo = getTimeAgo(contact.data_ultima_atualizacao);
  const whatsAppUrl = getWhatsAppUrl(contact.telefone);

  // Load score for this lead
  useEffect(() => {
    if (clientId && (contact.session_id || contact.id)) {
      supabase
        .from('lead_analysis')
        .select('score')
        .eq('client_id', clientId)
        .eq('lead_session_id', contact.session_id || contact.id)
        .maybeSingle()
        .then(({ data }) => {
          if (data?.score) setScore(data.score);
        });
    }
  }, [clientId, contact.session_id, contact.id]);

  // Calculate score for individual lead
  const handleCalculateScore = async (e: React.MouseEvent) => {
    e.stopPropagation();
    if (!clientId) return;
    
    setCalculatingScore(true);
    try {
      const leadData = {
        session_id: contact.session_id || contact.id,
        nome_cliente: contact.nome_cliente,
        telefone: contact.telefone,
        temperatura_lead: contact.temperatura_lead,
        etapa_atual: contact.etapa_atual,
        categoria_produto: contact.categoria_produto,
        data_primeira_mensagem: contact.data_primeira_mensagem,
        data_ultima_atualizacao: contact.data_ultima_atualizacao,
        observacoes: contact.observacoes,
      };

      const { data, error } = await supabase.functions.invoke('calculate-lead-scores', {
        body: { client_id: clientId, leads: [leadData] }
      });

      if (error) throw error;

      // Reload score
      const { data: newScore } = await supabase
        .from('lead_analysis')
        .select('score')
        .eq('client_id', clientId)
        .eq('lead_session_id', contact.session_id || contact.id)
        .maybeSingle();

      if (newScore?.score) {
        setScore(newScore.score);
        toast.success('Score calculado!');
      }
    } catch (error: any) {
      console.error('Error calculating score:', error);
      toast.error('Erro ao calcular score');
    } finally {
      setCalculatingScore(false);
    }
  };

  const scoreStyle = score !== null ? getScoreStyle(score) : null;

  // List View Mode
  if (viewMode === 'list') {
    return (
      <div
        onClick={onClick}
        className="glass rounded-lg px-4 py-3 cursor-pointer transition-all hover:bg-secondary/50 hover:border-primary/50 grid grid-cols-5 gap-4 items-center"
      >
        <div className="flex items-center gap-2 min-w-0">
          <span className="text-sm font-medium text-foreground truncate">
            {contact.nome_cliente || 'Sem nome'}
          </span>
        </div>
        <div className="text-sm text-muted-foreground truncate">
          {contact.telefone || '-'}
        </div>
        <div className="text-sm text-muted-foreground truncate">
          {contact.categoria_produto || '-'}
        </div>
        <div className="text-sm text-muted-foreground truncate">
          {contact.etapa_atual || '-'}
        </div>
        <div className="flex items-center gap-2">
          {/* Score badge in list view */}
          {scoreStyle && score !== null && (
            <span 
              className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-bold border"
              style={{
                background: scoreStyle.bg,
                borderColor: scoreStyle.border,
                color: scoreStyle.color
              }}
            >
              <Zap size={10} />
              {score}
            </span>
          )}
          {tempConfig && (
            <span className={cn(
              "inline-flex items-center px-2 py-0.5 rounded-full text-xs font-semibold border",
              tempConfig.className
            )}>
              {tempConfig.emoji}
            </span>
          )}
          {timeAgo && (
            <span className="text-[10px] text-muted-foreground/60">{timeAgo}</span>
          )}
        </div>
      </div>
    );
  }

  // ==========================================
  // KANBAN VIEW - PREMIUM CARD
  // ==========================================
  return (
    <div 
      onClick={onClick}
      className="group relative mb-3 cursor-pointer transition-all duration-300 ease-out"
      style={{
        transform: 'translateY(0)',
      }}
      onMouseEnter={(e) => {
        const target = e.currentTarget as HTMLDivElement;
        target.style.transform = 'translateY(-4px) scale(1.02)';
      }}
      onMouseLeave={(e) => {
        const target = e.currentTarget as HTMLDivElement;
        target.style.transform = 'translateY(0) scale(1)';
      }}
    >
      {/* Main Card */}
      <div 
        className="relative rounded-xl overflow-hidden border transition-all duration-300"
        style={{
          background: 'linear-gradient(135deg, hsl(var(--card)) 0%, hsl(var(--secondary) / 0.5) 100%)',
          borderColor: `${tempColor}30`,
          boxShadow: '0 4px 20px rgba(0, 0, 0, 0.3)',
        }}
        onMouseEnter={(e) => {
          const target = e.currentTarget as HTMLDivElement;
          target.style.boxShadow = `0 12px 40px rgba(0, 0, 0, 0.4), 0 0 20px ${tempColor}30`;
          target.style.borderColor = `${tempColor}60`;
        }}
        onMouseLeave={(e) => {
          const target = e.currentTarget as HTMLDivElement;
          target.style.boxShadow = '0 4px 20px rgba(0, 0, 0, 0.3)';
          target.style.borderColor = `${tempColor}30`;
        }}
      >
        {/* Left Color Bar */}
        <div 
          className="absolute left-0 top-0 bottom-0 w-1 transition-all duration-300 group-hover:w-1.5"
          style={{ 
            background: `linear-gradient(180deg, ${tempColor} 0%, ${stageColor} 100%)`
          }}
        />

        {/* Corner Glow Effect */}
        <div 
          className="absolute -top-4 -right-4 w-16 h-16 rounded-full opacity-20 blur-xl transition-opacity group-hover:opacity-40"
          style={{ background: tempColor }}
        />

        {/* Content */}
        <div className="relative p-4 pl-5">
          {/* Header */}
          <div className="flex justify-between items-start gap-2 mb-3">
            <div className="flex-1 min-w-0">
              <h3 className="text-sm font-semibold text-foreground leading-tight line-clamp-1 group-hover:text-primary transition-colors">
                {contact.nome_cliente || 'Sem nome'}
              </h3>
              {contact.telefone && (
                <span className="text-xs text-muted-foreground flex items-center gap-1 mt-0.5">
                  <Phone size={12} className="text-muted-foreground" />
                  {String(contact.telefone)}
                </span>
              )}
            </div>

            {tempConfig && (
              <span 
                className="px-2 py-1 rounded-lg text-[10px] font-bold border shrink-0 transition-transform group-hover:scale-110"
                style={{
                  background: `${tempConfig.color}20`,
                  borderColor: `${tempConfig.color}40`,
                  color: tempConfig.color
                }}
              >
                {tempConfig.emoji} {contact.temperatura_lead}
              </span>
            )}
          </div>

          {/* Score and Time in Stage Indicators */}
          <div className="flex items-center gap-2 mb-3 flex-wrap">
            {/* Score Badge or Calculate Button */}
            {scoreStyle && score !== null ? (
              <div 
                className="flex items-center gap-1 px-2 py-1 rounded-md text-[10px] font-bold border"
                style={{
                  background: scoreStyle.bg,
                  borderColor: scoreStyle.border,
                  color: scoreStyle.color
                }}
              >
                <Zap size={10} />
                Score: {score}
              </div>
            ) : clientId && (
              <button
                onClick={handleCalculateScore}
                disabled={calculatingScore}
                className="flex items-center gap-1 px-2 py-1 rounded-md text-[10px] font-medium border bg-primary/10 border-primary/30 text-primary hover:bg-primary/20 transition-colors disabled:opacity-50"
              >
                {calculatingScore ? (
                  <Loader2 size={10} className="animate-spin" />
                ) : (
                  <Zap size={10} />
                )}
                {calculatingScore ? 'Calculando...' : 'Calcular Score'}
              </button>
            )}
            
            {/* Time in Stage */}
            <div className="flex items-center gap-1.5 px-2 py-1 rounded-md bg-secondary/50">
              <Timer size={12} className="text-muted-foreground" />
              <span className="text-[10px] text-muted-foreground font-medium">
                {getTimeInStage(contact)} nesta etapa
              </span>
            </div>
          </div>

          {/* Recipient */}
          {contact.nome_destinatario && (
            <div className="mb-2 px-2 py-1.5 rounded-lg bg-primary/10 border border-primary/20">
              <span className="text-xs text-primary flex items-center gap-1.5">
                <Gift size={12} />
                Para: {contact.nome_destinatario}
              </span>
            </div>
          )}

          {/* Product */}
          {(contact.categoria_produto || contact.item_pedido) && (
            <div className="flex items-center gap-1.5 text-xs text-muted-foreground mb-2">
              <Package size={12} className="shrink-0 text-primary/70" />
              <span className="truncate">
                {contact.categoria_produto}
                {contact.item_pedido && ` - ${contact.item_pedido}`}
              </span>
            </div>
          )}

          {/* Observations */}
          {contact.observacoes && (
            <div className="mb-3 p-2 rounded-lg bg-secondary/30 border border-border/20">
              <p className="text-[11px] text-muted-foreground italic line-clamp-2">
                {contact.observacoes}
              </p>
            </div>
          )}

          {/* WhatsApp Action Button */}
          {whatsAppUrl && (
            <div className="pt-2 border-t border-border/20">
              <a
                href={whatsAppUrl}
                target="_blank"
                rel="noopener noreferrer"
                onClick={(e) => e.stopPropagation()}
                className="w-full flex items-center justify-center gap-2 px-3 py-2 rounded-lg text-xs font-semibold transition-all duration-200"
                style={{
                  background: 'rgba(37, 211, 102, 0.15)',
                  border: '1px solid rgba(37, 211, 102, 0.3)',
                  color: '#25D366',
                }}
                onMouseEnter={(e) => {
                  const target = e.currentTarget as HTMLAnchorElement;
                  target.style.background = 'rgba(37, 211, 102, 0.25)';
                  target.style.transform = 'scale(1.02)';
                }}
                onMouseLeave={(e) => {
                  const target = e.currentTarget as HTMLAnchorElement;
                  target.style.background = 'rgba(37, 211, 102, 0.15)';
                  target.style.transform = 'scale(1)';
                }}
                title="Abrir WhatsApp"
              >
                <WhatsAppIcon size={14} />
                WhatsApp
              </a>
            </div>
          )}

          {/* Footer with time ago */}
          {timeAgo && (
            <div className="flex justify-end mt-2">
              <span className="text-[9px] text-muted-foreground italic flex items-center gap-1">
                <Clock size={9} />
                {timeAgo}
              </span>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
